import { FC, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Card, FancyLink, Loader, Message } from "shared/components";
import { IdCard } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";
import { getUserCardActions, useFetchingUserDocuments } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import { useFullUserProfileContext } from "Bands/contexts";
import { IStatus } from "shared/types";
import { Passports, TravelVisas, SocialSecurityForms, DrivingPermits, Vehicles } from ".";

const {
  profile: { profileIds, documents },
} = copy;

interface Props {
  canManageProfile?: boolean;
}

export const Documents: FC<Props> = ({ canManageProfile }) => {
  const history = useHistory();

  const { passports, travelVisas, socialSecurityForms, drivingPermits, vehicles } =
    useFullUserProfileContext();

  const { fetchingUserDocuments } = useFetchingUserDocuments();

  const handleEdit = useCallback(() => {
    history.push(BAND_PATHS.EDIT_PROFILE_DOCUMENTS);
  }, [history]);

  const actions = useMemo(
    () =>
      canManageProfile
        ? getUserCardActions({
            handleEdit,
          })
        : [],
    [canManageProfile, handleEdit]
  );

  const content = useMemo(() => {
    if (fetchingUserDocuments) return <Loader />;
    if (
      !passports?.length &&
      !travelVisas?.length &&
      !socialSecurityForms?.length &&
      !drivingPermits?.length &&
      !vehicles?.length
    ) {
      return (
        <Message status={IStatus.warning}>
          {canManageProfile ? (
            <FancyLink to={BAND_PATHS.EDIT_PROFILE_DOCUMENTS} status={IStatus.warning}>
              {documents.empty}
            </FancyLink>
          ) : (
            documents.emptyView
          )}
        </Message>
      );
    }

    return (
      <Spacer size="l">
        <Passports />
        <TravelVisas />
        <SocialSecurityForms />
        <DrivingPermits />
        <Vehicles />
      </Spacer>
    );
  }, [
    canManageProfile,
    drivingPermits?.length,
    fetchingUserDocuments,
    passports?.length,
    socialSecurityForms?.length,
    travelVisas?.length,
    vehicles?.length,
  ]);

  return (
    <Card
      id={profileIds.documents}
      title={documents.title}
      icon={IdCard}
      actions={actions}
      actionDataTestId="user_details_actions"
    >
      {content}
    </Card>
  );
};
